/**
 * The section shell every block in the app sits in.
 *
 * A hairline border, a titled header with room for a figure or a control on the
 * right, a quiet body, and an optional footer inside the border. The rings and
 * the due-date strip are the only things allowed to be loud, so the containers
 * around them are deliberately plain.
 *
 * This is one component on purpose. It replaced a per-feature copy on the
 * dashboard and the account screen that had already drifted apart on font
 * weight and header gap — a difference nobody chose and everybody could see.
 */
import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface PanelProps {
  /** Monospace label above the title — what family of figure this block holds. */
  eyebrow?: string
  title: string
  /** One line under the title. Say what the block shows, not what it is called. */
  description?: ReactNode
  /** Right-hand slot: a total, a count, a link, a button. */
  actions?: ReactNode
  /** A row of figures or badges under the header, inside its own rule. */
  meta?: ReactNode
  /** Sits below the body, inside the border — used for legends and footnotes. */
  footer?: ReactNode
  /** Removes the body padding, for blocks that manage their own gutters. */
  flush?: boolean
  children: ReactNode
  className?: string
  bodyClassName?: string
}

export function Panel({
  eyebrow,
  title,
  description,
  actions,
  meta,
  footer,
  flush = false,
  children,
  className,
  bodyClassName,
}: PanelProps) {
  const headingId = useId()

  return (
    <section
      aria-labelledby={headingId}
      className={cn('border-hairline bg-panel flex min-w-0 flex-col rounded-lg border', className)}
    >
      <header className="border-hairline flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b px-4 py-3">
        <div className="min-w-0">
          {eyebrow && <p className="eyebrow mb-1.5">{eyebrow}</p>}
          <h2 id={headingId} className="font-display text-sm font-semibold">
            {title}
          </h2>
          {description && (
            <p className="text-dim mt-0.5 max-w-prose text-xs text-pretty">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </header>

      {meta && (
        <div className="border-hairline flex flex-wrap items-center gap-x-6 gap-y-2 border-b px-4 py-2.5">
          {meta}
        </div>
      )}

      <div className={cn('min-w-0 flex-1', flush ? '' : 'p-4', bodyClassName)}>{children}</div>

      {footer && <div className="border-hairline border-t px-4 py-2.5">{footer}</div>}
    </section>
  )
}
